import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Plus, Edit2, Trash2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { usePermissions } from '../../contexts/PermissionContext';

export default function QuoteStatusSettings() {
  const { isAdmin } = usePermissions();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState({ name: '', color: '#3b82f6', order: 10 });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    loadStatuses();
  }, []);

  const loadStatuses = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/account-statuses/quotes', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStatuses(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load statuses:', error);
      toast.error('Failed to load quote statuses');
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newStatus.name.trim()) {
      toast.error('Status name is required');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.post('/api/account-statuses/quotes', newStatus, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Status added successfully');
      setNewStatus({ name: '', color: '#3b82f6', order: 10 });
      loadStatuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add status');
    }
  };

  const handleEdit = async (status) => {
    if (status.is_fixed) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.put(`/api/account-statuses/quote/${status.id}`, editData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Status updated successfully');
      setEditingId(null);
      setEditData({});
      loadStatuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this status?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/account-statuses/quote/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Status deleted successfully');
      loadStatuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete status');
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="quote-status-settings">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Quote Status Settings</h1>
        <p className="text-muted-foreground mt-2">Manage quote statuses (fixed + custom)</p>
      </div>

      {/* Add New Status - Admin Only */}
      {isAdmin() && (
      <Card>
        <CardHeader>
          <CardTitle>Add New Custom Status</CardTitle>
          <CardDescription>Create additional statuses beyond the fixed ones</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={newStatus.name}
                onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
                placeholder="e.g., Under Review"
                data-testid="new-status-name"
              />
            </div>
            <div>
              <Label htmlFor="color">Color</Label>
              <Input
                id="color"
                type="color"
                value={newStatus.color}
                onChange={(e) => setNewStatus({ ...newStatus, color: e.target.value })}
                data-testid="new-status-color"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleAdd} className="w-full" data-testid="add-status-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Status
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Existing Statuses */}
      <Card>
        <CardHeader>
          <CardTitle>All Quote Statuses</CardTitle>
          <CardDescription>Fixed statuses (locked) + Your custom statuses</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {statuses.map((status) => (
              <div key={status.id} className="flex items-center gap-4 p-4 border rounded-lg" data-testid={`status-item-${status.id}`}>
                <div
                  className="w-6 h-6 rounded-full"
                  style={{ backgroundColor: status.color }}
                />
                {editingId === status.id ? (
                  <>
                    <Input
                      value={editData.name || status.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      type="color"
                      value={editData.color || status.color}
                      onChange={(e) => setEditData({ ...editData, color: e.target.value })}
                      className="w-20"
                    />
                    <Button size="sm" onClick={() => handleEdit(status)} data-testid={`save-status-${status.id}`}>
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 font-medium">{status.name}</span>
                    {status.is_fixed && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Lock className="h-3 w-3" />
                        Fixed
                      </Badge>
                    )}
                    {!status.is_fixed && isAdmin() && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingId(status.id);
                            setEditData({ name: status.name, color: status.color });
                          }}
                          data-testid={`edit-status-${status.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(status.id)}
                          data-testid={`delete-status-${status.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
